import { z } from 'zod'
import { parseISODate, parseTimestamp } from '@/util/zod/validators'
import { MedSummaryEntrySchema } from '@/medical-billing-summaries/entries/store/schemas'
import { MedSummaryReferenceSchema } from '@/medical-billing-summaries/references/store/schemas'

// Mirrors backend paxton_app/med_summary/asset/schemas.py + the update/file status enums.

export enum MedSummaryUpdateKind {
  INITIALIZE = 'INITIALIZE',
  ADD_FILES = 'ADD_FILES',
  REMOVE_FILE = 'REMOVE_FILE',
}

// PENDING -> EXTRACTING -> MERGING -> FINALIZING -> DONE is the happy path; FAILED_FILES holds
// the run until the user retries or completes excluding failures; ERROR left the live
// billing summary untouched.
export enum MedSummaryUpdateStatus {
  PENDING = 'PENDING',
  EXTRACTING = 'EXTRACTING',
  MERGING = 'MERGING',
  FINALIZING = 'FINALIZING',
  DONE = 'DONE',
  FAILED_FILES = 'FAILED_FILES',
  ERROR = 'ERROR',
}

export const kActiveMedSummaryUpdateStatuses = [
  MedSummaryUpdateStatus.PENDING,
  MedSummaryUpdateStatus.EXTRACTING,
  MedSummaryUpdateStatus.MERGING,
  MedSummaryUpdateStatus.FINALIZING,
]

export enum MedSummaryAssetFileStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  EXTRACTED = 'EXTRACTED',
  INCLUDED = 'INCLUDED',
  FAILED = 'FAILED',
}

export enum FileAddDisposition {
  NEW = 'NEW',
  ALREADY_INCLUDED = 'ALREADY_INCLUDED',
  FAILED_WILL_RETRY = 'FAILED_WILL_RETRY',
  NOT_READY = 'NOT_READY',
  NOT_IN_MATTER = 'NOT_IN_MATTER',
  UNSUPPORTED_TYPE = 'UNSUPPORTED_TYPE',
}

// Amounts come over the wire as Decimal serializations (string or number).
const AmountSchema = z.preprocess((val) => (val === null ? null : typeof val === 'string' ? parseFloat(val) : val), z.number())

export const MedSummaryUpdateReadSchema = z.object({
  id: z.string().uuid(),
  asset_id: z.string().uuid(),
  user_id: z.string(),
  kind: z.nativeEnum(MedSummaryUpdateKind),
  status: z.nativeEnum(MedSummaryUpdateStatus),
  requested_file_ids: z.array(z.string().uuid()).default([]),
  removed_file_id: z.string().uuid().nullable(),
  files_added: z.number().int().nullable(),
  entries_added: z.number().int().nullable(),
  entries_removed: z.number().int().nullable(),
  files_unchanged: z.number().int().nullable(),
  files_failed: z.number().int().nullable(),
  error_reason: z.string().nullable(),
  workflow_version: z.number().int().nullable(),
  completed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type MedSummaryUpdateRead = z.infer<typeof MedSummaryUpdateReadSchema>

export const MedSummaryUpdateHistoryResponseSchema = z.object({
  asset_id: z.string().uuid(),
  updates: z.array(MedSummaryUpdateReadSchema),
})
export type MedSummaryUpdateHistoryResponse = z.infer<typeof MedSummaryUpdateHistoryResponseSchema>

export const MedSummaryAssetOverviewSchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  summary_version: z.number().int(),
  workflow_version: z.number().int().nullable(),
  last_built_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  total_entries: z.number().int(),
  total_files: z.number().int(),
  active_update: MedSummaryUpdateReadSchema.nullable(),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type MedSummaryAssetOverview = z.infer<typeof MedSummaryAssetOverviewSchema>

// Get-or-empty response: asset is null when the matter has no billing summary yet.
export const MedSummaryAssetOverviewResponseSchema = z.object({
  asset: MedSummaryAssetOverviewSchema.nullable(),
})
export type MedSummaryAssetOverviewResponse = z.infer<typeof MedSummaryAssetOverviewResponseSchema>

export const MedSummaryAssetEntriesResponseSchema = z.object({
  asset_id: z.string().uuid(),
  summary_version: z.number().int(),
  total_entries: z.number().int(),
  total_amount: AmountSchema,
  entries: z.array(MedSummaryEntrySchema),
  references: z.record(z.string().uuid(), MedSummaryReferenceSchema),
})
export type MedSummaryAssetEntriesResponse = z.infer<typeof MedSummaryAssetEntriesResponseSchema>

export const MedSummaryAssetFileViewSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  status: z.nativeEnum(MedSummaryAssetFileStatus),
  inclusion_index: z.number().int(),
  last_processed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  entries_extracted: z.number().int(),
  error_reason: z.string().nullable(),
  // Set for non-PDF sources: the PDF the file was extracted against (AI-94). Citation
  // highlight geometry is relative to this conversion, so the viewer opens it.
  converted_pdf_path: z.string().nullable(),
})
export type MedSummaryAssetFileView = z.infer<typeof MedSummaryAssetFileViewSchema>

export const MedSummaryAssetFilesResponseSchema = z.object({
  asset_id: z.string().uuid(),
  files: z.array(MedSummaryAssetFileViewSchema),
})
export type MedSummaryAssetFilesResponse = z.infer<typeof MedSummaryAssetFilesResponseSchema>

export const PossibleDuplicateSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
})
export type PossibleDuplicate = z.infer<typeof PossibleDuplicateSchema>

export const FileAddPreviewItemSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string().nullable(),
  disposition: z.nativeEnum(FileAddDisposition),
  possible_duplicate_of: PossibleDuplicateSchema.nullable(),
})
export type FileAddPreviewItem = z.infer<typeof FileAddPreviewItemSchema>

export const AddFilesPreviewResponseSchema = z.object({
  asset_id: z.string().uuid(),
  items: z.array(FileAddPreviewItemSchema),
})
export type AddFilesPreviewResponse = z.infer<typeof AddFilesPreviewResponseSchema>

export const RemovalImpactResponseSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  entries_count: z.number().int(),
  total_amount: AmountSchema,
  earliest_service_date: z.preprocess((val, ctx) => (val === null ? null : parseISODate(val, ctx)), z.string().nullable()),
  latest_service_date: z.preprocess((val, ctx) => (val === null ? null : parseISODate(val, ctx)), z.string().nullable()),
})
export type RemovalImpactResponse = z.infer<typeof RemovalImpactResponseSchema>

export const RemoveFileResponseSchema = z.object({
  asset_id: z.string().uuid(),
  user_file_id: z.string().uuid(),
  entries_removed: z.number().int(),
  total_entries: z.number().int(),
  total_files: z.number().int(),
  summary_version: z.number().int(),
})
export type RemoveFileResponse = z.infer<typeof RemoveFileResponseSchema>

export const InitializeSummaryResponseSchema = z.object({
  asset: MedSummaryAssetOverviewSchema,
  update: MedSummaryUpdateReadSchema,
})
export type InitializeSummaryResponse = z.infer<typeof InitializeSummaryResponseSchema>

export const ApplyUpdateResponseSchema = z.object({
  update: MedSummaryUpdateReadSchema,
})
export type ApplyUpdateResponse = z.infer<typeof ApplyUpdateResponseSchema>

export const UpdateProgressSchema = z.object({
  files_total: z.number().int(),
  files_pending: z.number().int(),
  files_processing: z.number().int(),
  files_extracted: z.number().int(),
  files_included: z.number().int(),
  files_failed: z.number().int(),
})
export type UpdateProgress = z.infer<typeof UpdateProgressSchema>

export const FailedFileSummarySchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  error_reason: z.string().nullable(),
})
export type FailedFileSummary = z.infer<typeof FailedFileSummarySchema>

// update.id doubles as the user-facing error ID when the run ends in ERROR.
export const UpdateStatusResponseSchema = z.object({
  update: MedSummaryUpdateReadSchema,
  progress: UpdateProgressSchema,
  failed_files: z.array(FailedFileSummarySchema),
})
export type UpdateStatusResponse = z.infer<typeof UpdateStatusResponseSchema>

export const ConvertedPdfViewUrlResponseSchema = z.object({
  asset_id: z.string().uuid(),
  user_file_id: z.string().uuid(),
  viewable_url: z.string(),
  expires_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type ConvertedPdfViewUrlResponse = z.infer<typeof ConvertedPdfViewUrlResponseSchema>

// Request payloads (validated server-side; plain types here).
export type InitializeSummaryRequest = {
  matter_id: string
  user_file_ids: string[]
}

export type AddFilesPreviewRequest = {
  user_file_ids: string[]
}

export type ApplyUpdateRequest = {
  add_user_file_ids: string[]
}
